import { useMemo } from "react";

import {
  useGetBillingOverview,
  useGetBillingPlans,
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetElitePerks,
  useGetOwnerDetails,
  useGetOwners,
} from "~/v2/api/hooks";
import { isPaperOrOnboarding } from "~/v2/utils";
import { getPrettyCash } from "~/v2/utils/formatting";

// raw price -> price from API in cents
// price -> price in dollars
export type MarketDataPlans = {
  monthly: {
    id: string;
    price: number;
    rawPrice: number;
    displayPrice: string;
  };
  yearly: {
    id: string;
    price: number;
    rawPrice: number;
    displayPrice: string;
    discount: number;
  };
};

export const useGetMarketDataSubscriptionDetails = () => {
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();

  const { details } = useGetOwnerDetails();

  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: account?.id ?? "",
    options: { enabled: !!account?.id },
  });

  const { isLegalEntity, legalEntityOwner, accountOpener } = useMemo(() => {
    const primaryOwner = owners?.find((owner) => owner?.primary === true);
    const isLegalEntity = primaryOwner?.is_legal_entity ?? false;
    const legalEntityOwner = owners?.find((details) => details.is_legal_entity);
    const accountOpener = owners?.find(
      (details) => details.is_legal_account_opener,
    );

    return {
      isLegalEntity,
      legalEntityOwner,
      accountOpener,
    };
  }, [owners]);

  const { perks: elitePerks, isLoading: isElitePerksLoading } =
    useGetElitePerks({
      accountID: account?.id ?? "",
      options: {
        enabled: !!account?.id && !isPaperOrOnboarding(account),
      },
    });

  const { plans, isLoading: isPlansLoading } = useGetBillingPlans({
    options: { enabled: !!account?.id },
  });

  const marketDataPlans: MarketDataPlans | undefined =
    !plans?.monthly || !plans.yearly
      ? undefined
      : {
          monthly: {
            id: plans.monthly.id,
            price: plans.monthly.price / 100,
            rawPrice: plans.monthly.price,
            displayPrice: getPrettyCash(plans.monthly.price / 100, {
              acceptZero: true,
            }),
          },
          yearly: {
            id: plans.yearly.id,
            price: plans.yearly.price / 100,
            rawPrice: plans.yearly.price,
            displayPrice: getPrettyCash(plans.yearly.price / 100, {
              acceptZero: true,
            }),
            discount: plans.monthly.price / 100,
          },
        };

  const { billing, isLoading: isBillingLoading } = useGetBillingOverview({
    options: { enabled: !!account?.id },
  });

  const isElite = !!elitePerks && elitePerks.elite_status === "100k";

  const isSubscribed = !!billing?.plan || !!elitePerks?.market_data;

  const isLoading =
    !account ||
    !details ||
    isElitePerksLoading ||
    isBillingLoading ||
    isOwnersLoading ||
    isPlansLoading;

  return {
    account,
    details,
    elitePerks,
    billing,
    plans: marketDataPlans,
    isSubscribed,
    isElite,
    isLegalEntity,
    legalEntityOwner,
    accountOpener,
    isLoading,
  };
};

export type MarketDataSubscriptionDetailsReturn = ReturnType<
  typeof useGetMarketDataSubscriptionDetails
>;
